import { Flex, Select, Typography } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { t } from "i18next";
import { forwardRef, useImperativeHandle, useState } from "react";

const { Title, Text } = Typography;

const timeOptions = [
  { value: 12, label: t('common.timeOption12') },
  { value: 24, label: t('common.timeOption24') },
  { value: 36, label: t('common.timeOption36') },
  { value: 48, label: t('common.timeOption48') },
  { value: 72, label: t('common.timeOption72') },
  { value: 96, label: t('common.timeOption96') },
  { value: 120, label: t('common.timeOption120') },
];

interface TimeRangeSelectorProps {
  onTimeChange?: (deadlineTime: Dayjs) => void;
  initialHours?: number;
  ddlTime?: number;
}

export interface TimeRangeSelectorRef {
  getDeadlineTime: () => Dayjs;
  resetTime: () => void;
}

export const TimeRangeSelector = forwardRef<
  TimeRangeSelectorRef,
  TimeRangeSelectorProps
>(({ initialHours = 24, ddlTime }, ref) => {
  const [selectedHours, setSelectedHours] = useState<number>(initialHours);
  const [customHours, setCustomHours] = useState<string>("");

  const currentTime = dayjs(ddlTime);
  const roundedTime =
    currentTime.minute() > 0 || currentTime.second() > 0
      ? currentTime.add(1, "hour").startOf("hour")
      : currentTime.startOf("hour");

  const deadlineTime = roundedTime.add(
    customHours ? parseInt(customHours, 10) : selectedHours,
    "hour"
  );

  useImperativeHandle(ref, () => ({
    getDeadlineTime: () => deadlineTime,
    resetTime: () => {
      setSelectedHours(initialHours);
      setCustomHours("");
    }
  }));

  return (
    <>
      <div style={{ marginBottom: 24 }}>
        <Title level={4}>
          {t(ddlTime ? 'requirement.curLazy' : 'common.currentTime')}: {currentTime.format("YYYY-MM-DD HH:mm:ss")}
        </Title>
      </div>

      <Flex vertical gap={16}>
        <Flex gap={6} align="center">
          <Text>{t(ddlTime ? 'requirement.lazyTime' : 'common.selectTime')}:</Text>
          <Select
            style={{ width: 120 }}
            value={customHours ? undefined : selectedHours}
            onChange={(value) => {
              setSelectedHours(value);
              setCustomHours("");
            }}
            options={timeOptions}
          />
          <span>{t('common.or')}</span>
          <input
            type="number"
            min="1"
            value={customHours}
            onChange={(e) => {
              setCustomHours(e.target.value);
              if (e.target.value) setSelectedHours(0);
            }}
            placeholder={t('common.inputHours')}
            style={{
              width: 100,
              padding: "4px 8px",
              border: "1px solid #d9d9d9",
              borderRadius: 4,
            }}
          />
          <span>{t('common.hour')}</span>
        </Flex>

        <div>
          <Text strong>{t('common.finalEnd')}:</Text>
          <div style={{ marginTop: 8, fontSize: 16 }}>
            {deadlineTime.format("YYYY-MM-DD HH:mm:ss")}
          </div>
          <div style={{ color: "#666", marginTop: 4 }}>
            ({t('common.upper')} {roundedTime.format("HH:mm")} {t('common.plus')}{" "}
            {customHours || selectedHours} {t('common.hour')})
          </div>
        </div>
      </Flex>
    </>
  );
});

TimeRangeSelector.displayName = "TimeRangeSelector";